/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { message, Space } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type FinderApplicationPrivateModuleSpecsFragment } from '../../../../api/fragments/FinderApplicationPrivateModuleSpecs';
import {
    UpdateFinderApplicationPrivateModuleDocument,
    type UpdateFinderApplicationPrivateModuleMutation,
    type UpdateFinderApplicationPrivateModuleMutationVariables,
} from '../../../../api/mutations/updateFinderApplicationPrivateModule';
import { type FinderApplicationPrivateModuleUpdateSettings, PreferenceValue } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import { hasAppointmentScenario, hasPaymentScenario } from '../modules/implementations/shared';
import CounterSettingDetails from '../shared/CounterSettingDetails';
import { getFinancingApplicationMarket } from '../StandardApplicationModulePage/StandardApplicationModuleMainDetails';
import { getValidator } from './FinderApplicationModuleConfiguration';
import FinderApplicationModuleConfigurationForm from './FinderApplicationModuleConfigurationForm';
import { type FinderApplicationModuleCounterKey } from './type';

const counterFields: FinderApplicationModuleCounterKey[] = [
    'leadCounter',
    'applicationCounter',
    'reservationCounter',
    'appointmentCounter',
    'insuranceCounter',
];

type FinderApplicationModuleConfigurationProps = {
    module: FinderApplicationPrivateModuleSpecsFragment;
};

const FinderApplicationModuleConfiguration = ({ module }: FinderApplicationModuleConfigurationProps) => {
    const { t } = useTranslation('finderApplicationModuleDetails');
    const apolloClient = useApolloClient();
    const validate = useValidator(getValidator(false));

    const initialValues: FinderApplicationPrivateModuleUpdateSettings = useMemo(
        () => ({
            ...pick(
                [
                    'displayName',
                    'testDrive',
                    'tradeIn',
                    'isTradeInAmountVisible',
                    'scenarios',
                    'depositPrice',
                    'paymentSettingsId',
                    'myInfoSettingId',
                    'liveChatSettingId',
                    'priceDisclaimer',
                    'reservationInstructions',
                    'promoCodeModuleId',
                    'market',
                    'showResetKYCButton',
                    'showFromValueOnVehicleDetails',
                    'bankDisplayPreference',
                    'finderVehicleConditions',
                    'porscheApprovedInfo',
                    'displayAppointmentDatepicker',
                    'appointmentModuleId',
                    'insurerDisplayPreference',
                    'insurerIds',
                    'insuranceModuleId',
                    'financingPreference',
                    'isInsuranceOptional',
                    'displayFinanceCalculator',
                    'showInsuranceCalculator',
                    'isOcrEnabled',
                    'showVisitModelPageButton',
                    'modelPageUrl',
                    'reservationPeriod',
                    'skipForDeposit',
                    'showRemoteFlowButtonInKYCPage',
                    'hasDealerOptions',
                    'includeDealerOptionsForFinancing',
                    'flexibleDiscount',
                    'bankIds',
                    'capModuleId',
                    'isSearchCapCustomerOptional',
                    'capPrequalification',
                    'leadOrigin',
                    'leadMedium',
                    'leadCampaignId',
                    ...counterFields,
                ],
                module
            ),
            marketType: getFinancingApplicationMarket(module.marketType),
            bankModuleId: module?.bankModule?.id,
        }),
        [module]
    );

    const onSubmit = useHandleError(
        async (values: FinderApplicationPrivateModuleUpdateSettings) => {
            const { scenarios } = values;
            message.loading({
                content: t('finderApplicationModuleDetails:messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            const settings: FinderApplicationPrivateModuleUpdateSettings = {
                ...pick(
                    [
                        'displayName',
                        'tradeIn',
                        'isTradeInAmountVisible',
                        'scenarios',
                        'myInfoSettingId',
                        'liveChatSettingId',
                        'priceDisclaimer',
                        'reservationInstructions',
                        'promoCodeModuleId',
                        'market',
                        'marketType',
                        'bankModuleId',
                        'showResetKYCButton',
                        'showFromValueOnVehicleDetails',
                        'bankDisplayPreference',
                        'finderVehicleConditions',
                        'porscheApprovedInfo',
                        'displayAppointmentDatepicker',
                        'appointmentModuleId',
                        'insurerDisplayPreference',
                        'insurerIds',
                        'insuranceModuleId',
                        'financingPreference',
                        'isInsuranceOptional',
                        'displayFinanceCalculator',
                        'showInsuranceCalculator',
                        'isOcrEnabled',
                        'showVisitModelPageButton',
                        'modelPageUrl',
                        'reservationPeriod',
                        'skipForDeposit',
                        'showRemoteFlowButtonInKYCPage',
                        'hasDealerOptions',
                        'includeDealerOptionsForFinancing',
                        'flexibleDiscount',
                        'bankIds',
                        'capModuleId',
                        'isSearchCapCustomerOptional',
                        'capPrequalification',
                        'leadOrigin',
                        'leadMedium',
                        'leadCampaignId',
                    ],
                    values
                ),
                testDrive: hasAppointmentScenario(scenarios),
                paymentSettingsId: null,
                depositPrice: null,
                bankModuleId:
                    values.displayFinanceCalculator === PreferenceValue.Yes ||
                    values.displayFinanceCalculator === PreferenceValue.Optional
                        ? values.bankModuleId
                        : null,
                capModuleId: values.capModuleId || null,
            };

            if (hasPaymentScenario(scenarios)) {
                // assign value if deposit
                settings.paymentSettingsId = values.paymentSettingsId;
                settings.depositPrice = values.depositPrice;
            }

            await apolloClient
                .mutate<
                    UpdateFinderApplicationPrivateModuleMutation,
                    UpdateFinderApplicationPrivateModuleMutationVariables
                >({
                    mutation: UpdateFinderApplicationPrivateModuleDocument,
                    variables: { moduleId: module.id, settings },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('finderApplicationModuleDetails:messages.success'),
                key: 'primary',
            });
        },
        [apolloClient, module.id, t]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="updateForm" name="updateForm" onSubmitCapture={handleSubmit}>
                    <Space direction="vertical">
                        <FinderApplicationModuleConfigurationForm module={module} />
                        <CounterSettingDetails names={counterFields} />
                    </Space>
                </Form>
            )}
        </Formik>
    );
};

export default FinderApplicationModuleConfiguration;
